import { Alignment, BoxModelConstraint, ColumnComposition, ColumnCompositions, MultipleLineTextRenderer } from '@blk/simba';
import { CellData, TabData, TableData } from '@ddt-web/data-service';
import { IconAndTextRenderer } from '@ddt-web/pdf-utils';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';

export class DefaultColumnComposition {
	protected textSize: string = 'text-8';
	protected boxModel = { paddingLeft: 5, paddingRight: 5 };
	protected firstColumnBoxModel = { paddingRight: 5 };

	protected getHeaderRowCompositions(headers: Array<CellData>, columnProportions: Array<number>, subTextInHeader?: string): Array<ColumnComposition> {
		return headers.map(
			(header: CellData, index: number) => {
				const headerCell = {
					cells: [header],
					widthProportion: columnProportions[index],
					textClasses: ['text-bold', this.textSize],
					horizontalAlignment: !!index ? Alignment.Horizontal.Right : Alignment.Horizontal.Left,
					verticalAlignment: Alignment.Vertical.Bottom,
					boxModelConstraint: !!index ? this.boxModel : this.firstColumnBoxModel
				};
				const headerCellWithSubText = {
					renderer: new MultipleLineTextRenderer(
						[
							{
								cell: header,
								textClasses: ['text-8', 'text-bold']
							},
							{
								cell: subTextInHeader,
								textClasses: ['text-7', 'text-grey']
							}
						],
						Alignment.Horizontal.Right,
						Alignment.Vertical.Bottom,
						columnProportions[index],
						this.boxModel
					)
				};
				return new ColumnComposition((!!index && subTextInHeader) ? headerCellWithSubText : headerCell);
			}
		);
	}

	public getColumnCompositions(
		tabData: TabData,
		tableColumnProportions?: Array<number>,
		isFirstColumnBold: boolean = false,
		boxModelConstraint?: Partial<BoxModelConstraint>,
		subTextInHeader?: string
	): ColumnCompositions {
		const tableData: TableData = get(tabData, 'tableData[0]');
		const tableHeaders: Array<CellData> = [tableData.firstColumnTitle, ...tableData.headersX];
		const categories: Array<CellData> = tableData.headersY.filter((header: CellData) => header.config && header.config.visible);
		const columnProportions: Array<number> = tableColumnProportions || this.getColumnProportions(tabData);

		return new ColumnCompositions(
			this.getHeaderRowCompositions(tableHeaders, columnProportions, subTextInHeader),
			tableData.cells
				.filter((row: Array<CellData>) => row[0].config && row[0].config.visible)
				.map((row: Array<CellData>, index: number) => {
					const firstCol = new ColumnComposition({
						cells: categories[index],
						widthProportion: columnProportions[0],
						textClasses: isFirstColumnBold ? ['text-bold', this.textSize] : [this.textSize],
						horizontalAlignment: Alignment.Horizontal.Left,
						verticalAlignment: Alignment.Vertical.Middle,
						boxModelConstraint: this.firstColumnBoxModel,
						renderer: categories[index].attribute.iconType
							? new IconAndTextRenderer(categories[index], 10, 10, Alignment.Horizontal.Left, columnProportions[0], [13, 87])
							: null
					});


					if (!isEmpty(tableColumnProportions) && tableColumnProportions[0] === 100) {
						return [firstCol];
					} else {
						return [
							firstCol,
							...row.map(
								(cell: CellData, cellIndex: number) =>
									new ColumnComposition({
										cells: [cell],
										widthProportion: columnProportions[cellIndex + 1],
										textClasses: [this.textSize],
										verticalAlignment: Alignment.Vertical.Middle,
										boxModelConstraint: boxModelConstraint || this.boxModel
									})
							)];
					}
				})
		);
	}

	protected getColumnProportions(tabData: TabData): Array<number> {
		const tableData: TableData = get(tabData, 'tableData[0]');
		const headers: Array<CellData> = tabData.displayConfig.transpose ? tableData.headersX : tableData.headersY;
		let columnProportions: Array<number>;

		switch (headers.length) {
			case 0:
				columnProportions = [100];
				break;
			case 1:
				columnProportions = [50, 50];
				break;
			case 2:
				columnProportions = [40, 30, 30];
				break;
			case 3:
				columnProportions = [35.5, 21.5, 21.5, 21.5];
				break;
			case 4:
				columnProportions = [40, 15, 15, 15, 15];
				break;
			case 5:
				columnProportions = [30, 14, 14, 14, 14, 14];
				break;
			case 6:
				columnProportions = [16, 14, 14, 14, 14, 14, 14];
				break;
			case 7:
				columnProportions = [16, 12, 12, 12, 12, 12, 12, 12];
				break;
		}
		return columnProportions;
	}
}
